/**
 * DataAgentDetailsModal Component
 *
 * Modal for viewing data agent details.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { Alert } from '../../../components/ui/Alert';
import { DataAgentStatusBadge } from './DataAgentStatusBadge';
import { useDataAgentDetails } from '../hooks/useDataAgentDetails';
import type { DataAgent } from '../../../types/data-agent.types';
import { DATA_AGENT_CONFIG_DEFAULTS } from '../../../types/data-agent.types';
import { getErrorMessage } from '../../../lib/utils/errors';

// Helper to format milliseconds as human-readable duration
const formatDuration = (ms: number): string => {
  if (ms < 60000) {
    return `${Math.round(ms / 1000)} seconds`;
  } else if (ms < 3600000) {
    return `${Math.round(ms / 60000)} minutes`;
  } else {
    const hours = Math.round(ms / 3600000);
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
};

// Helper to format large numbers
const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

interface DataAgentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  dataAgentId: string;
  onGenerateToken: (dataAgent: DataAgent) => void;
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{children}</dd>
  </div>
);

export const DataAgentDetailsModal: React.FC<DataAgentDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  dataAgentId,
  onGenerateToken,
}) => {
  const { data: dataAgent, isLoading, error } = useDataAgentDetails(tenantId, dataAgentId);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleString();
  };

  const hasToken = dataAgent?.tokenGeneratedAt && !dataAgent?.tokenRevokedAt;
  const isTokenExpired = dataAgent?.tokenExpiresAt && dataAgent.tokenExpiresAt * 1000 < Date.now();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Data Agent Details</Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {dataAgent && (
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
              <dl className="border-t border-gray-200">
                <DetailRow label="Name">{dataAgent.dataAgentName}</DetailRow>
                <DetailRow label="Code">
                  <span className="font-mono text-sm">{dataAgent.dataAgentCode}</span>
                </DetailRow>
                <DetailRow label="Description">
                  {dataAgent.description || <span className="text-gray-400">No description</span>}
                </DetailRow>
                <DetailRow label="Status">
                  <DataAgentStatusBadge status={dataAgent.status} />
                </DetailRow>
                <DetailRow label="Active">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      dataAgent.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {dataAgent.isActive ? 'Yes' : 'No'}
                  </span>
                </DetailRow>
              </dl>
            </div>

            {/* Token Info */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Token Information</h3>
              <dl className="border-t border-gray-200">
                <DetailRow label="Token Status">
                  {hasToken ? (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isTokenExpired
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {isTokenExpired ? 'Expired' : 'Active'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {dataAgent.tokenRevokedAt ? 'Revoked' : 'No Token'}
                    </span>
                  )}
                </DetailRow>
                <DetailRow label="Token Generated">{formatDate(dataAgent.tokenGeneratedAt)}</DetailRow>
                <DetailRow label="Token Expires">{formatTimestamp(dataAgent.tokenExpiresAt)}</DetailRow>
                {dataAgent.tokenRevokedAt && (
                  <DetailRow label="Token Revoked">{formatDate(dataAgent.tokenRevokedAt)}</DetailRow>
                )}
              </dl>
            </div>

            {/* Configuration */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Configuration</h3>
              <dl className="border-t border-gray-200">
                <DetailRow label="Heartbeat Interval">
                  {formatDuration(dataAgent.config?.heartbeatIntervalMs ?? DATA_AGENT_CONFIG_DEFAULTS.heartbeatIntervalMs)}
                </DetailRow>
                <DetailRow label="Poll Interval (Idle)">
                  {formatDuration(dataAgent.config?.pollIntervalMs ?? DATA_AGENT_CONFIG_DEFAULTS.pollIntervalMs)}
                </DetailRow>
                <DetailRow label="Poll Interval (Busy)">
                  {formatDuration(dataAgent.config?.pollIntervalBusyMs ?? DATA_AGENT_CONFIG_DEFAULTS.pollIntervalBusyMs)}
                </DetailRow>
                <DetailRow label="Query Timeout">
                  {formatDuration(dataAgent.config?.queryTimeoutMs ?? DATA_AGENT_CONFIG_DEFAULTS.queryTimeoutMs)}
                </DetailRow>
                <DetailRow label="Max Rows Per Query">
                  {formatNumber(dataAgent.config?.maxRowsPerQuery ?? DATA_AGENT_CONFIG_DEFAULTS.maxRowsPerQuery)} rows
                </DetailRow>
              </dl>
            </div>

            {/* Activity Info */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Activity</h3>
              <dl className="border-t border-gray-200">
                <DetailRow label="Last Heartbeat">{formatDate(dataAgent.lastHeartbeatAt)}</DetailRow>
                <DetailRow label="Created">{formatDate(dataAgent.createdAt)}</DetailRow>
                <DetailRow label="Last Updated">{formatDate(dataAgent.updatedAt)}</DetailRow>
              </dl>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button variant="primary" onClick={() => onGenerateToken(dataAgent)}>
                Manage Token
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
